import React from 'react';
import {
  ENTITY_LABELS,
  matchBadge,
  matchedDetail,
  shortDate,
  type SmartRow,
} from '../../utils/smartSearch';

/** One short line per result (REG-833). */
function RowContent({ row }: { row: SmartRow }) {
  if (row.kind === 'recent') {
    const r = row.recent;
    const parts = r.entity === 'deal'
      ? [`#${r.submission_number ?? r.entity_id}`, r.title, r.funder_name, r.status]
      : [r.title];
    return <span className="smart-row-main">{parts.filter(Boolean).join(' · ')}</span>;
  }
  const hit = row.hit;
  const badge = matchBadge(hit);
  let main: React.ReactNode;
  if (hit.entity === 'email') {
    // Subject, then who sent it (to whom, when a recipient matched) and when.
    const who = hit.matched_field === 'to' ? hit.recipient ?? hit.sender : hit.sender;
    main = (
      <>
        <span className="smart-row-title">{hit.title || '(No subject)'}</span>
        {who && <span className="smart-row-meta">{who}</span>}
        {hit.sent_at && <span className="smart-row-meta">{shortDate(hit.sent_at)}</span>}
        {hit.email_source === 'instantly' && <span className="smart-row-meta">Instantly</span>}
      </>
    );
  } else if (hit.entity === 'deal') {
    main = (
      <>
        <span className="smart-row-number">#{hit.submission_number ?? hit.entity_id}</span>
        <span className="smart-row-title">{hit.title}</span>
        {hit.funder_name && <span className="smart-row-meta">{hit.funder_name}</span>}
        {hit.status && <span className="smart-row-meta">{hit.status}</span>}
      </>
    );
  } else {
    const detail = hit.entity === 'merchant' || hit.entity === 'lead' ? matchedDetail(hit) : null;
    main = (
      <>
        <span className="smart-row-title">{hit.title}</span>
        {detail && <span className="smart-row-meta">{detail}</span>}
        {hit.entity !== 'merchant' && hit.status && <span className="smart-row-meta">{hit.status}</span>}
      </>
    );
  }
  return (
    <>
      <span className="smart-row-main">{main}</span>
      {badge && <span className="smart-row-badge">{badge}</span>}
    </>
  );
}

interface SmartHitListProps {
  rows: SmartRow[];
  highlighted: number;
  onHighlight: (index: number) => void;
  onSelect: (row: SmartRow) => void;
  isFetching?: boolean;
  query: string;
  footer?: React.ReactNode;
  emptyText?: string;
}

export function SmartHitList({ rows, highlighted, onHighlight, onSelect, isFetching, query, footer, emptyText }: SmartHitListProps) {
  let lastGroup = '';
  const showingRecents = rows.length > 0 && rows[0].kind === 'recent';
  return (
    <div
      className="smart-dropdown"
      role="listbox"
      data-testid="smart-search-results"
      data-state={isFetching ? 'loading' : 'ready'}
      onMouseDown={(e) => e.preventDefault()}
    >
      {rows.length === 0 && query.trim() !== '' && !isFetching && (
        <div className="smart-empty">{emptyText ?? `No matches for "${query.trim()}"`}</div>
      )}
      {rows.map((row, index) => {
        const group = row.kind === 'recent'
          ? 'Recent'
          : row.hit.section === 'suggestion'
            ? 'Did you mean'
            : ENTITY_LABELS[row.hit.entity];
        const header = group !== lastGroup ? group : null;
        lastGroup = group;
        const key = row.kind === 'hit' ? `${row.hit.section}-${row.hit.entity}-${row.hit.email_source ?? ''}-${row.hit.entity_id}` : `recent-${row.recent.entity}-${row.recent.entity_id}`;
        return (
          <React.Fragment key={key}>
            {header && (
              <div className={`smart-group${header === 'Did you mean' ? ' smart-group-suggestion' : ''}`}>{header}</div>
            )}
            <div
              role="option"
              aria-selected={index === highlighted}
              data-testid="smart-search-row"
              data-entity={row.kind === 'hit' ? row.hit.entity : row.recent.entity}
              data-email-source={row.kind === 'hit' ? row.hit.email_source : undefined}
              className={`smart-row${index === highlighted ? ' active' : ''}${row.kind === 'hit' && row.hit.section === 'suggestion' ? ' suggestion' : ''}`}
              onMouseEnter={() => onHighlight(index)}
              onClick={() => onSelect(row)}
            >
              <RowContent row={row} />
            </div>
          </React.Fragment>
        );
      })}
      {(footer || showingRecents) && <div className="smart-footer">{footer}</div>}
    </div>
  );
}
